import { useEffect, useRef, useState } from "react";
import { checkUsername, setUsername as apiSetUsername, type PublicUser } from "../lib/api";

// Shown right after a user's first sign-in (when they have no username yet). Picking a
// username is required because it's the basis for human-readable app URLs
// (quikpl.ai/<username>/<slug>) and the creator profile page. Pre-fills with a server
// suggestion derived from their email/name.

type CheckState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available"; username: string }
  | { kind: "taken" }
  | { kind: "invalid"; reason: string };

export function UsernameModal({
  suggested,
  onDone,
}: {
  suggested: string | null;
  onDone: (user: PublicUser) => void;
}) {
  const [value, setValue] = useState(suggested ?? "");
  const [check, setCheck] = useState<CheckState>({ kind: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced availability check as the user types.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const v = value.trim();
    if (!v) {
      setCheck({ kind: "idle" });
      return;
    }
    setCheck({ kind: "checking" });
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await checkUsername(v);
        if (!r.valid) setCheck({ kind: "invalid", reason: r.reason ?? "invalid username" });
        else if (r.available) setCheck({ kind: "available", username: r.username! });
        else setCheck({ kind: "taken" });
      } catch {
        setCheck({ kind: "idle" });
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const canSubmit = check.kind === "available" && !submitting;

  async function onSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { user } = await apiSetUsername(value.trim());
      onDone(user);
    } catch (e) {
      setError((e as Error).message ?? "failed to set username");
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-bg">
      <div className="modal">
        <h2>Pick a username</h2>
        <div className="row">
          <div className="hint" style={{ marginTop: 0, marginBottom: 12 }}>
            This is how your apps are shared — <span className="mono">quikpl.ai/&lt;username&gt;/your-app</span>.
            Lowercase letters, digits, and hyphens. You can&rsquo;t change it easily later, so pick well.
          </div>
          <div className="username-field">
            <span className="username-prefix">quikpl.ai/</span>
            <input
              autoFocus
              value={value}
              placeholder="your-name"
              onChange={(e) => setValue(e.target.value.toLowerCase())}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) onSubmit();
              }}
            />
          </div>
          <div className="username-status">
            {check.kind === "checking" && <span className="mono" style={{ color: "var(--fg-dim)" }}><span className="spinner" /> checking…</span>}
            {check.kind === "available" && <span className="mono status-ok">✓ available</span>}
            {check.kind === "taken" && <span className="mono status-err">✗ taken</span>}
            {check.kind === "invalid" && <span className="mono status-err">✗ {check.reason}</span>}
          </div>
          {error && <div className="mono status-err" style={{ fontSize: 12 }}>{error}</div>}
        </div>
        <div className="actions" style={{ justifyContent: "flex-end" }}>
          <button className="primary" onClick={onSubmit} disabled={!canSubmit}>
            {submitting ? <><span className="spinner" /> Saving…</> : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
